/* eslint-disable no-await-in-loop */
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useEffect, useState, useRef } from 'react';
// @mui
import {
  Grid,
  Container,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Card,
  CardHeader,
  Box,
  TextField,
  Stack,
  Divider,
  Alert,
  SpeedDial,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
// components
import { useSnackbar } from 'notistack';
import axiosInstance from '../../utils/axiosInstance';
// ----------------------------------------------------------------------

export default function ReplayStreamSetting() {
  const [vstSettings, setVstSettings] = useState(null);
  const endRef = useRef(null);
  const stunurlListRef = useRef(null);
  const staticTurnurlListRef = useRef(null);
  const hostList = useSelector((state) => state.app.hostList);
  const [settingFetchStatus, setSettingFetchStatus] = useState('uninitialized');
  const [vstDevice, setVstDevice] = useState(hostList.length > 0 ? hostList[0] : null);
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    setSettingFetchStatus('pending');
    axiosInstance
      .get(`${window.location.protocol}//${vstDevice}/api/v1/replay/configuration`)
      .then((response) => {
        setSettingFetchStatus('success');
        if (response.data) {
          setVstSettings(response.data);
        }
      })
      .catch((error) => {
        setSettingFetchStatus('error');
        enqueueSnackbar(`Error - Failed to get settings for ${window.adaptorType} ${vstDevice}`, {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        if (error.response.data == null)
          enqueueSnackbar(`Error - ${error.response.status}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
        else
          enqueueSnackbar(`Error - ${error.response.data.error_message}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
      });
    axiosInstance
      .get(`${window.location.protocol}//${vstDevice}/api/v1/replay/version`)
      .then((response) => {
        if (response.data) {
          console.log('type ', response.data.type);
          console.log('version ', response.data.version);
        }
      })
      .catch((error) => {
        console.log(`Failed to fetch ${window.adaptorType} version`, error);
      });
  }, [enqueueSnackbar, vstDevice]);

  const handleVstDeviceChange = (e) => {
    setVstSettings(null);
    setVstDevice(e.target.value);
  };

  const scrollToBottom = () => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getValue = (val) => {
    if(val == null || val === "") {
      return val;
    }
    return val.replace(/\s/g, '').split(',')
  }

  const handleSubmit = () => {
    let jsonData = {};
    try {
      jsonData = {
        stunUrlList: getValue(stunurlListRef.current.value),
        staticTurnUrlList: getValue(staticTurnurlListRef.current.value)
      };
    } catch (error) {
      enqueueSnackbar(`Error - Please enter comma separated valid strings`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
      return;
    }
    enqueueSnackbar(`Pending - Update ${window.adaptorType} ${vstDevice} settings`, {
      variant: 'info',
      anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
    });
    axiosInstance
      .post(`${window.location.protocol}//${vstDevice}/api/v1/replay/configuration`, jsonData)
      .then(() => {
        enqueueSnackbar(`Success - Updated ${window.adaptorType} ${vstDevice} settings`, {
          variant: 'success',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
      })
      .catch((error) => {
        setSettingFetchStatus('error');
        enqueueSnackbar(`Error - Failed to set settings for ${window.adaptorType} ${vstDevice}`, {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        if (error.response.data == null)
          enqueueSnackbar(`Error - ${error.response.status}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
        else
          enqueueSnackbar(`Error - ${error.response.data.error_message}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
      });
  };

  const replayStreamSettingPresent = vstDevice != null && vstSettings != null;

  return (
    <>
      <Helmet>
        <title> Replay stream settings </title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 5 }}>
          Replay Stream
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardHeader title={`${window.adaptorType} devices`} subheader={`Select ${window.adaptorType} device`} />
              <Box sx={{ p: 3, pb: 1 }} dir="ltr">
                <FormControl>
                  <RadioGroup row name="vst-device-radio-button" value={vstDevice} onChange={handleVstDeviceChange}>
                    {hostList.map((host) => (
                      <FormControlLabel
                        value={host}
                        control={<Radio />}
                        label={`${window.adaptorType} ${host}`}
                        key={host}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>
              <Box sx={{ p: 3 }} dir="ltr">
                {vstDevice != null && vstSettings != null && settingFetchStatus !== 'pending' && (
                  <Alert severity="success">{`${
                    Object.keys(vstSettings).length
                  } settings found for ${vstDevice}`}</Alert>
                )}
                {vstDevice != null && vstSettings == null && settingFetchStatus !== 'pending' && (
                  <Alert severity="error">{`Failed to get ${window.adaptorType} settings for ${vstDevice}`}</Alert>
                )}
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Card>
              <Box sx={{ p: 3 }}>
                <Stack spacing={3} sx={{ mb: 2 }}>
                  {replayStreamSettingPresent && Object.prototype.hasOwnProperty.call(vstSettings, 'stunUrlList') && (
                    <TextField
                      name={'stunUrlList'}
                      label={'stunUrlList'}
                      defaultValue={vstSettings.stunUrlList}
                      key={'stunUrlList'}
                      inputRef={stunurlListRef}
                    />
                  )}
                  {replayStreamSettingPresent &&
                    Object.prototype.hasOwnProperty.call(vstSettings, 'staticTurnUrlList') && (
                      <TextField
                        name={'staticTurnUrlList'}
                        label={'staticTurnUrlList'}
                        defaultValue={vstSettings.staticTurnUrlList}
                        key={'staticTurnUrlList'}
                        inputRef={staticTurnurlListRef}
                      />
                    )}
                  {replayStreamSettingPresent && (
                    <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end">
                      <LoadingButton size="large" type="submit" variant="contained" onClick={handleSubmit}>
                        Submit
                      </LoadingButton>
                    </Box>
                  )}
                  {replayStreamSettingPresent && (
                    <Divider sx={{ my: 2 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Below options are not editable
                      </Typography>
                    </Divider>
                  )}
                  {replayStreamSettingPresent &&
                    Object.keys(vstSettings).map(
                      (setting) =>
                        setting !== 'stunUrlList' &&
                        setting !== 'staticTurnUrlList' && (
                          <TextField
                            name={setting}
                            label={setting}
                            defaultValue={vstSettings[setting]}
                            key={setting}
                            disabled
                          />
                        )
                    )}
                </Stack>
              </Box>
            </Card>
          </Grid>
          <SpeedDial
            ariaLabel="SpeedDial openIcon example"
            sx={{ position: 'fixed', bottom: 16, right: 16 }}
            onClick={scrollToBottom}
            icon={<ArrowDownwardIcon />}
          />
        </Grid>
        <div ref={endRef} />
      </Container>
    </>
  );
}
